import isObject from 'lodash-es/isObject';
import round from 'lodash-es/round';
import uniqueId from 'lodash-es/uniqueId';

import { ROUND_PRECISION, SPLIT_NAMES, STATUSES } from '@/constants';
import { Money } from '@/models/money';
import { LineItem as OrderLineItem } from '@/models/order';
import { useUserStore } from '@/piniaStores/user/user.store';
import { getLowestPrice } from '@/utils/commerce';

/**
 * Model for the LineItem in an Order. It contains the Product,
 * quantity, and pricing info at time of order
 */
export default class LineItem implements OrderLineItem {
  public batch;
  public inventoryItem;
  public isBackorder;
  public isSample;
  public productId;
  public product;
  public quantity;
  public wholesalePrice; // the regular price for this item
  public salePrice; // the sale price for this item
  public orderedUnitPrice; // used to display the new wholesale price
  public currency;
  public tax; // note: set in InvoiceTotals
  public total; // note: updated in orderRow
  public soldByCase;
  public unitsPerCase;
  public notes;
  private isAncillary; // inherit this prop from the Product to make it easier to ref
  private allowFractionalQuantities;
  private availableInventory;
  private exceedsQuantity; // used to mark the lineItem
  private unitOfMeasure;

  constructor(item, status?: string) {
    if (!item || !item.product) {
      return;
    }

    let {
      quantity = 0,
      // eslint-disable-next-line prefer-const
      product,
    } = item;

    const calculateLineItemInventory = () => {
      const isBatchInventoryEnabled = useUserStore().getIsSplitEnabled(SPLIT_NAMES.INVENTORY_BATCH);
      const isUnlimitedInventory = item.product.inventoryManagement === STATUSES.INVENTORY_MANAGEMENT.UNLIMITED;

      // Use the inventory item quantity when a product batch is selected when adding a line item.
      // Requires batch inventory to be enabled.
      if (isBatchInventoryEnabled && item.inventoryItem?.quantityInProductUom) {
        return item.inventoryItem.quantityInProductUom;
      }

      return isUnlimitedInventory ? Number.POSITIVE_INFINITY : Number(product.availableInventory || 0);
    };

    quantity = Number(quantity);
    const inventory = calculateLineItemInventory();
    const canBackorder = status ? status === 'Draft' : true;
    const availableInventory = round(canBackorder ? inventory : quantity + inventory, ROUND_PRECISION.INVENTORY);
    const unitsPerCase = item.unitMultiplier || product.unitMultiplier;

    const { amount: orderedUnitPrice = 0, currency } = this.#getOrderValue(item) || {};
    const salePrice = item.salePrice ? this.#getPrice(item.salePrice) : this.#getPrice(product.salePrice) || 0;

    const lineItemPrice = getLowestPrice(orderedUnitPrice, salePrice);

    this.isSample = product.listingState?.toUpperCase() === STATUSES.PRODUCT_LISTING_STATE.SAMPLE;
    this.allowFractionalQuantities = product.allowFractionalQuantities || false;

    Object.assign(this, {
      ...item,
      availableInventory,
      exceedsQuantity: item.exceedsQuantity,
      isAncillary: product.isAncillary, // used for tax stuffs in InvoiceTotals
      isBackorder: item.isBackorder || quantity > availableInventory,
      notes: item.notes || '',
      orderedUnitPrice,
      currency,
      quantity,
      salePrice,
      soldByCase: item.soldByCase || false,
      total: (lineItemPrice / unitsPerCase) * quantity,
      unitDenomination: product.unitDenomination,
      unitsPerCase,
      unitOfMeasure: product.unitOfMeasure,
      uniqueRowId: item.id || uniqueId(), // if the lineItem has an id use it as uniqueRowId, otherwise use a lodash generated uniqueId.
      wholesalePrice: product.wholesalePrice?.amount,
    });
  }

  #getOrderValue(item: OrderLineItem) {
    if (isObject(item?.orderedUnitPrice)) {
      return item.orderedUnitPrice as Money;
    }

    if (isObject(item.product?.priceSchedulePrice)) {
      return item.product.priceSchedulePrice as Money;
    }

    if (isObject(item.product?.wholesalePrice)) {
      return item.product.wholesalePrice as Money;
    }

    return null;
  }

  #getPrice(price: Money | number): number {
    return isObject(price) ? price.amount : price;
  }
}
